
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown, Target } from 'lucide-react';
import Image1 from "../../assets/travel.webp";
import Image2 from "../../assets/travel2.webp";
import Image3 from "../../assets/travel3.webp";
import Image4 from "../../assets/travel5.webp";
import Image5 from "../../assets/travel4.webp";

const Travel = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(0);

  const handleSignupClick = () => {
    navigate("/login?mode=signup");
  };

  const solutions = [
    {
      title: "Instant Booking Confirmations",
      description:
        "Provide travelers with immediate peace of mind. Send automated SMS confirmations for flights, hotels, and car rentals the moment the 'book' button is pressed."
    },
    {
      title: "Real-Time Flight & Gate Updates",
      description:
        "Keep passengers informed of delays, gate changes, or cancellations. Our high-priority gateway ensures critical travel updates reach passengers before they even reach the airport."
    },
    {
      title: "Mobile Boarding Passes & Check-in",
      description:
        "Simplify the departure process by sending secure check-in links and digital boarding passes directly to the traveler's mobile device via SMS."
    },
    {
      title: "Personalized Travel Alerts",
      description:
        "Enhance the journey with value-added info like weather updates at the destination, local travel tips, or exclusive lounge access offers based on their itinerary."
    },
    {
      title: "24/7 Passenger Support",
      description:
        "Enable two-way messaging so travelers can request assistance, change seats, or ask questions about baggage policies in real-time without waiting on hold."
    }
  ];

  // Travel-specific workflow tags
  const row1 = ["Flight Confirmed", "Gate Change Alert", "Check-in Reminder", "Boarding Pass Sent", "Hotel Booking", "Airport Transfer", "Lounge Access"];
  const row2 = ["Visa Status Update", "Local Weather Alert", "Excursion Booking", "Payment Reminder", "Travel Insurance", "Feedback Request", "Welcome Home"];

  return (
    <div className="w-full font-sans text-slate-900 overflow-x-hidden">
      {/* Premium Animation Styles */}
      <style>
        {`
            @keyframes scroll-left {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
            }
            @keyframes scroll-right {
                0% { transform: translateX(-50%); }
                100% { transform: translateX(0); }
            }
            .animate-scroll-left {
                display: flex;
                width: max-content;
                animation: scroll-left 40s linear infinite;
            }
            .animate-scroll-right {
                display: flex;
                width: max-content;
                animation: scroll-right 40s linear infinite;
            }
            .pause-on-hover:hover {
                animation-play-state: paused;
            }
        `}
      </style>

      {/* --- SECTION 1: HERO SECTION --- */}
      <section className="bg-[#EEF2FC] flex flex-col md:flex-row items-center justify-between px-6 py-10 md:px-12 lg:px-24 min-h-[600px]">
        <div className="w-full md:w-1/2 mb-10 md:mb-0 md:pr-10 pt-12 md:pt-1 flex flex-col items-start -mt-16">
          <h1 className="text-4xl md:text-5xl font-medium text-slate-900 leading-tight tracking-tight mb-6">
            Effortless Travel Experiences with Eurofox Bulk SMS
          </h1>
          <p className="text-lg text-slate-700 mb-8 leading-relaxed">
            Enhance the passenger journey with instant booking confirmations,
            real-time flight updates, and personalized travel alerts.
            Keep your travelers informed and stress-free at every mile.
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center gap-2 px-6 py-3 text-white bg-slate-900 rounded-lg transition duration-300 shadow-md hover:bg-slate-800"
          >
            Request a Demo
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <div className="w-full md:w-1/2 flex justify-center">
          <img
            src={Image1}
            alt="Effortless Travel Experiences"
            className="relative w-auto max-w-lg object-contain -left-[10px] -top-[8px] h-[480px] rounded-xl"
          />
        </div>
      </section>

      {/* --- SECTION 2: TRAVEL PROCESSES --- */}
      <section className="bg-[#EEF2FC] py-14 md:py-20 lg:py-32 -mt-10 md:-mt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center max-w-4xl mx-auto mb-10 md:mb-16 lg:mb-24">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-medium text-slate-900 leading-tight">
              How Eurofox can help <br />
              <span className="text-[#F46653]">
                Travel processes
              </span>
            </h2>
          </div>

          <div className="flex flex-col lg:row-reverse lg:flex-row items-center gap-8 md:gap-12 lg:gap-16">
            <div className="lg:w-[630px] w-full">
              <div className="relative rounded-2xl overflow-hidden shadow-lg w-full h-[240px] sm:h-[300px] lg:h-[530px]">
                <img
                  src={Image2}
                  alt="Eurofox Travel Process"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>

            <div className="lg:w-7/12 w-full space-y-4">
              {solutions.map((item, index) => (
                <div
                  key={index}
                  className="bg-white border border-slate-200 rounded-2xl shadow-sm transition-all duration-300"
                >
                  <button
                    onClick={() =>
                      setOpenIndex(openIndex === index ? -1 : index)
                    }
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors rounded-2xl"
                  >
                    <span className="font-medium text-slate-800 text-lg pr-4">
                      {item.title}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${openIndex === index ? "rotate-180" : ""
                        }`}
                    />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ${openIndex === index
                      ? "max-h-60 opacity-100"
                      : "max-h-0 opacity-0"
                      }`}
                  >
                    <div className="px-6 pb-6 text-slate-600 text-base leading-relaxed border-t border-slate-100 pt-4">
                      {item.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- SECTION 3: WHY TRAVEL BUSINESSES CHOOSE EUROFOX --- */}
      <section className="bg-[#EEF2FC] py-16 md:py-24 lg:py-28">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-20">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-medium text-slate-900 leading-tight mb-6 -mt-24">
              Smart Travel Messaging
              <span className="text-[#F46653]"> Solutions by Eurofox</span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed">
              Eurofox Bulk SMS helps travel brands send timely, personalized updates that boost passenger satisfaction and streamline operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-[#F8FAFF] rounded-2xl shadow-sm hover:shadow-md transition duration-300 overflow-hidden">
              <img
                src={Image3}
                alt="Airline Notifications"
                className="w-full h-[350px] object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  Airline & Airport Notifications
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Deliver instant departure reminders, boarding alerts,
                  gate changes, and emergency updates directly to passengers'
                  mobile phones with high-priority SMS routing.
                </p>
              </div>
            </div>

            <div className="bg-[#F8FAFF] rounded-2xl shadow-sm hover:shadow-md transition duration-300 overflow-hidden">
              <img
                src={Image4}
                alt="Travel Agency Booking Updates"
                className="w-full h-[350px] object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  Travel Agency Engagement
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Automate itinerary confirmations, payment reminders,
                  visa notifications, and exclusive travel offers to build
                  stronger relationships and increase repeat bookings.
                </p>
              </div>
            </div>

            <div className="bg-[#F8FAFF] rounded-2xl shadow-sm hover:shadow-md transition duration-300 overflow-hidden">
              <img
                src={Image5}
                alt="Tour Operator Customer Support"
                className="w-full h-[350px] object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  Tour & Hospitality Communication
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Keep guests informed with hotel check-in details,
                  local excursion updates, weather alerts, and real-time
                  support — ensuring a smooth and memorable travel experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- SECTION 4: PREMIUM CONVERSATIONAL WORKFLOWS (TRAVEL) --- */}
      <section className="bg-[#EEF2FC] py-16 md:py-24 lg:py-32 border-t border-slate-100 overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-medium text-slate-900 mb-6 tracking-tight mt-0 lg:-mt-24 leading-tight">
            Faster updates, smoother journeys — <span className="text-[#F46653]">SMS solutions  for modern travel </span>
          </h2>

          <div className="relative mt-10 mb-10 lg:mt-20 lg:mb-20 overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-12 md:w-20 lg:w-40 z-10 bg-gradient-to-r from-[#EEF2FC] to-transparent"></div>
            <div className="absolute inset-y-0 right-0 w-12 md:w-20 lg:w-40 z-10 bg-gradient-to-l from-[#EEF2FC] to-transparent"></div>

            <div className="animate-scroll-left flex gap-4 lg:gap-8 mb-4 lg:mb-8">
              {[...row1, ...row1].map((tag, idx) => (
                <div
                  key={idx}
                  className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-2xl lg:rounded-3xl text-slate-700 font-semibold text-sm md:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4"
                >
                  {tag}
                </div>
              ))}
            </div>

            <div className="animate-scroll-right flex gap-4 lg:gap-8">
              {[...row2, ...row2].map((tag, idx) => (
                <div
                  key={idx}
                  className="px-5 py-3 lg:px-10 lg:py-6 bg-white border border-slate-100 rounded-2xl lg:rounded-3xl text-slate-700 font-semibold text-sm md:text-base lg:text-xl whitespace-nowrap transition-all shadow-sm mx-2 lg:mx-4"
                >
                  {tag}
                </div>
              ))}
            </div>
          </div>

          <p className="text-lg md:text-xl lg:text-2xl text-slate-600 font-normal max-w-4xl mx-auto leading-relaxed mb-10 md:mb-16 px-4">
            Empower travel agencies and tour operators with intelligent SMS workflows that guide passengers, provide instant support, and ensure a stress-free travel experience.
          </p>

          <button
            onClick={handleSignupClick}
            className="inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 md:px-10 py-4 md:py-5 text-white bg-slate-900 rounded-xl transition duration-300 shadow-xl hover:shadow-2xl text-lg md:text-xl font-semibold"
          >
            Start Your Travel Journey
            <Target className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default Travel;